import { useEffect, useMemo, useState } from 'react';
import {
  Feature,
  isFeatureEnabled,
  isFeatureEnabledForCSAByManager,
} from '../../../config/FeaturesConfig';
import useInspireOptions from '../../../hooks/useInspireOptions';

/**
 *  This hook handles our phased rollout for CSA summaries and considers three main cases:
 *   1. You are in the pilot group: enabled
 *   2. You are not in the pilot and you are CSA: Check if your manager has the new rollout enabled
 *   3. You are not in the pilot and you are NOT a CSA. Check if you have the new rollout enabled
 */
const useIsCSASummariesEnabled = ({ managerId }: { managerId?: string }) => {
  const isInPilot = useMemo(() => {
    return isFeatureEnabled(Feature.CSA_SUMMARIES_PILOT);
  }, []);

  const isInRollout = useMemo(() => {
    return isFeatureEnabled(Feature.CSA_SUMMARIES_ROLLOUT);
  }, []);

  const isWCApproved = useMemo(() => {
    return isFeatureEnabled(Feature.CSA_SUMMARIES_WC_APPROVED);
  }, []);

  const { isOm, isTm } = useInspireOptions();
  const isCSA = !isOm && !isTm;

  const [isEnabledForManager, setIsEnabledForManager] = useState(false);

  useEffect(() => {
    if (!isInPilot && isCSA && managerId) {
      const checkManagerEligibility = async () => {
        const enabledForManager = await isFeatureEnabledForCSAByManager(
          Feature.CSA_SUMMARIES_ROLLOUT,
          managerId
        );
        setIsEnabledForManager(enabledForManager);
      };

      checkManagerEligibility();
    }
  }, [isInPilot, isCSA, managerId]);

  if (!isWCApproved) return false;

  const isEnabled =
    isInPilot || (isCSA && isEnabledForManager) || (!isCSA && isInRollout);

  return isEnabled;
};

export default useIsCSASummariesEnabled;
